import React, { useMemo } from "react";
import PropTypes from "prop-types";

/**
 * 16:9 SVG board with a night-sky background and 8 numbered stars.
 * Positions match Für Elise (E5, D#5, E5, D#5, E5, B4, D5, C5).
 * - Bigger stars
 * - Numbers slightly smaller (prop) so they fit nicely
 * - Completed path animates to the last collected star
 */
export default function ConstellationMap({
  collected = new Array(8).fill(false),
  backgroundUrl = "",
  className = "",
  animateCompleted = false,
  numberFontSize = 16,     // 👈 default smaller numbers
}) {
  // ---- Coordinate system (16:9) ----
  const VB_W = 1000;
  const VB_H = 562.5;

  // Horizontal positions
  const xPositions = useMemo(() => {
    const left = 90;
    const right = VB_W - 90;
    const step = (right - left) / 7;
    return Array.from({ length: 8 }, (_, i) => left + i * step);
  }, []);

  /**
   * Staff index reference (treble):
   *  E4=0,F4=1,G4=2,A4=3,B4=4,C5=5,D5=6,E5=7,F5=8
   * Für Elise:  E5(7), D#5(6.5), E5(7), D#5(6.5), E5(7), B4(4), D5(6), C5(5)
   */
  const staffIndexSequence = useMemo(
    () => [7, 6.5, 7, 6.5, 7, 4, 6, 5],
    []
  );

  // Larger vertical spacing (kept from previous)
  const staffTop = VB_H * 0.22;
  const stepPx = VB_H * 0.048;

  const yPositions = useMemo(
    () => staffIndexSequence.map((idx) => staffTop + (8 - idx) * stepPx),
    [staffTop, stepPx, staffIndexSequence]
  );

  // Full faint path
  const fullPathD = useMemo(() => {
    const pts = xPositions.map((x, i) => `${x},${yPositions[i]}`);
    return `M ${pts[0]} L ${pts.slice(1).join(" ")}`;
  }, [xPositions, yPositions]);

  // Completed path up to last collected star
  const lastCollectedIdx = useMemo(() => {
    let i = -1;
    for (let k = 0; k < collected.length; k += 1) {
      if (collected[k]) i = k;
      else break;
    }
    return i;
  }, [collected]);

  const completedPathD = useMemo(() => {
    if (lastCollectedIdx <= 0) return "";
    const pts = xPositions
      .slice(0, lastCollectedIdx + 1)
      .map((x, i) => `${x},${yPositions[i]}`);
    return `M ${pts[0]} L ${pts.slice(1).join(" ")}`;
  }, [xPositions, yPositions, lastCollectedIdx]);

  const outerR = 32; // star badge radius (unchanged—numbers got smaller)

  return (
    <div className={`w-full h-full ${className}`}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Background */}
        {backgroundUrl ? (
          <image href={backgroundUrl} x="0" y="0" width={VB_W} height={VB_H} />
        ) : (
          <rect x="0" y="0" width={VB_W} height={VB_H} fill="black" />
        )}

        {/* Faint full path */}
        <path
          d={fullPathD}
          fill="none"
          stroke="#9EC8F6"
          strokeWidth={9}
          opacity={0.45}
        />

        {/* Completed path (animated) */}
        {completedPathD && (
          <path
            d={completedPathD}
            fill="none"
            stroke="#9EC8F6"
            strokeWidth={11}
            opacity={0.9}
            pathLength={1}
            style={
              animateCompleted
                ? { strokeDasharray: 1, strokeDashoffset: 1 }
                : undefined
            }
          >
            {animateCompleted && (
              <animate
                attributeName="stroke-dashoffset"
                from="1"
                to="0"
                dur="0.9s"
                fill="freeze"
              />
            )}
          </path>
        )}

        {/* Stars */}
        {xPositions.map((x, i) => {
          const y = yPositions[i];
          const isOn = !!collected[i];
          const num = i + 1;

          if (isOn) {
            return (
              <g key={i} transform={`translate(${x}, ${y})`}>
                <circle r={outerR + 9} fill="rgba(0,0,0,0.15)" />
                <circle r={outerR} fill="#F6C14A" stroke="#E0A91D" strokeWidth="5" />
                <StarGlyph r={15} fill="#F9E8B0" stroke="#B98A17" />
                <text
                  y="5"
                  textAnchor="middle"
                  fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                  fontSize={numberFontSize}
                  fontWeight="900"
                  fill="#3b2a00"
                >
                  {num}
                </text>
              </g>
            );
          }

          return (
            <g key={i} transform={`translate(${x}, ${y})`}>
              <circle r={outerR + 7} fill="rgba(0,0,0,0.12)" />
              <circle r={outerR} fill="rgba(0,0,0,0.22)" stroke="#9EC8F6" strokeWidth="5" />
              <StarGlyph r={15} fill="none" stroke="#9EC8F6" />
              <text
                y="5"
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                fontSize={numberFontSize}
                fontWeight="900"
                fill="#9EC8F6"
              >
                {num}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

ConstellationMap.propTypes = {
  collected: PropTypes.arrayOf(PropTypes.bool),
  backgroundUrl: PropTypes.string,
  className: PropTypes.string,
  animateCompleted: PropTypes.bool,
  numberFontSize: PropTypes.number,
};

// 5-point star glyph
function StarGlyph({ r = 15, fill = "#fff", stroke = "#000" }) {
  const pts = [];
  const inner = r * 0.5;
  for (let i = 0; i < 10; i++) {
    const angle = (-90 + i * 36) * (Math.PI / 180);
    const radius = i % 2 === 0 ? r : inner;
    pts.push(`${Math.cos(angle) * radius},${Math.sin(angle) * radius}`);
  }
  return (
    <polygon
      points={pts.join(" ")}
      fill={fill}
      stroke={stroke}
      strokeWidth={1.7}
    />
  );
}
